// Lightweight branding helpers for the advisor portal login flow.
//
// Mirrors the client portal's `signup/brandContext` pattern, but scoped to
// the advisor portal sign-in surface. When the URL contains a firm alias or
// brand id (e.g. `/firm/clayvvest/login` or `/firm/brand-<uuid>/login`), the
// login page fetches the firm's public branding (logo + primary color) and
// caches it in localStorage so subsequent visits show the same branding even
// before the user authenticates and Redux hydrates the profile.

const ADVISOR_LOGIN_BRAND_CONTEXT_KEY = 'wavvest.advisorLoginBrandContext';
const BRAND_ID_PREFIX = 'brand-';
const ADVISOR_PUBLIC_AUTH_PATHS = new Set(['/login', '/reset-password', '/sign-up']);
const ADVISOR_FIRM_AUTH_PATH_PATTERN = /^\/firm\/([^/]+)\/(?:login|reset-password)$/;
const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export type AdvisorLoginBrandContext = {
  brand_id: string;
  name: string;
  introduction?: string;
  logo_uri: string;
  logo_mark_uri?: string;
  background_uri?: string;
  landing_color: string;
};

type AdvisorLoginBrandProfile = Partial<AdvisorLoginBrandContext> & {
  alias?: string | null;
};

type StoredAdvisorLoginBrandContext = {
  routeId: string;
  profile: AdvisorLoginBrandContext;
};

const sanitize = (value?: string | null): string => (typeof value === 'string' ? value.trim() : '');

const normalizePathname = (pathname: string): string => {
  const normalized = sanitize(pathname).replace(/\/+$/, '');
  return normalized || '/';
};

const safelyDecodeRouteSegment = (routeSegment: string): string => {
  try {
    return decodeURIComponent(routeSegment);
  } catch {
    return routeSegment;
  }
};

export const isAdvisorPublicAuthPath = (pathname: string): boolean => {
  const normalizedPathname = normalizePathname(pathname);
  return (
    ADVISOR_PUBLIC_AUTH_PATHS.has(normalizedPathname) ||
    ADVISOR_FIRM_AUTH_PATH_PATTERN.test(normalizedPathname)
  );
};

export const getAdvisorLoginRouteFirmId = (pathname: string, search = ''): string => {
  const normalizedPathname = normalizePathname(pathname);
  const firmRouteMatch = ADVISOR_FIRM_AUTH_PATH_PATTERN.exec(normalizedPathname);
  const firmRouteId = firmRouteMatch?.[1];

  if (firmRouteId) {
    return sanitize(safelyDecodeRouteSegment(firmRouteId));
  }

  if (!ADVISOR_PUBLIC_AUTH_PATHS.has(normalizedPathname)) {
    return '';
  }

  try {
    return sanitize(new URLSearchParams(search).get('firm'));
  } catch {
    return '';
  }
};

/**
 * Returns the URI only if it uses a safe scheme (https, http, or data).
 * Rejects javascript:, vbscript:, and other potentially unsafe schemes.
 */
export const isSafeImageUri = (uri: string): boolean => {
  const trimmed = uri.trim().toLowerCase();
  return (
    trimmed.startsWith('https://') ||
    trimmed.startsWith('http://') ||
    trimmed.startsWith('data:image/')
  );
};

export const getBrandIdFromFirmRouteId = (firmRouteId?: string | null): string => {
  const normalized = sanitize(firmRouteId);
  if (!normalized) {
    return '';
  }
  return normalized.startsWith(BRAND_ID_PREFIX) ? normalized : `${BRAND_ID_PREFIX}${normalized}`;
};

export const isBrandIdShapedRouteSegment = (firmRouteId?: string | null): boolean => {
  const normalized = sanitize(firmRouteId);
  if (!normalized) {
    return false;
  }
  const withoutPrefix = normalized.startsWith(BRAND_ID_PREFIX)
    ? normalized.slice(BRAND_ID_PREFIX.length)
    : normalized;
  return UUID_PATTERN.test(withoutPrefix);
};

export const buildAdvisorLoginBrandContext = (
  profile: Partial<AdvisorLoginBrandContext> | null | undefined,
): AdvisorLoginBrandContext | null => {
  if (!profile) {
    return null;
  }

  const brandId = sanitize(profile.brand_id);
  const introduction = sanitize(profile.introduction);
  const logoUri = sanitize(profile.logo_uri);
  const logoMarkUri = sanitize(profile.logo_mark_uri);
  const backgroundUri = sanitize(profile.background_uri);
  const landingColor = sanitize(profile.landing_color);
  const name = sanitize(profile.name);

  // Only persist a context when we actually got something useful from the
  // branding API; if every field is empty we don't want to mask real defaults.
  if (
    !brandId &&
    !introduction &&
    !logoUri &&
    !logoMarkUri &&
    !backgroundUri &&
    !landingColor &&
    !name
  ) {
    return null;
  }

  return {
    brand_id: brandId,
    name,
    introduction,
    logo_uri: logoUri,
    logo_mark_uri: logoMarkUri,
    background_uri: backgroundUri,
    landing_color: landingColor,
  };
};

export const persistAdvisorLoginBrandContext = (
  routeId: string,
  profile: AdvisorLoginBrandContext | null,
) => {
  // Null profile means we want to drop the cache entirely. Firm-specific URLs
  // only read matching route ids, while bare auth pages use the last
  // authenticated firm's cache to preserve the branded logout-to-login flow.
  if (!profile) {
    clearAdvisorLoginBrandContext();
    return;
  }

  try {
    const payload: StoredAdvisorLoginBrandContext = {
      routeId: sanitize(routeId),
      profile,
    };
    localStorage.setItem(ADVISOR_LOGIN_BRAND_CONTEXT_KEY, JSON.stringify(payload));
  } catch (error) {
    console.error('Failed to persist advisor login brand context:', error);
  }
};

export const persistAdvisorLoginBrandContextFromProfile = (
  profile: AdvisorLoginBrandProfile | null | undefined,
  options: { landingColor?: string } = {},
): boolean => {
  if (!profile) {
    return false;
  }

  const routeId =
    sanitize(profile.alias) ||
    (profile.brand_id ? getBrandIdFromFirmRouteId(profile.brand_id) : '');
  if (!routeId) {
    return false;
  }

  const landingColor = Object.prototype.hasOwnProperty.call(options, 'landingColor')
    ? options.landingColor
    : profile.landing_color;

  const next = buildAdvisorLoginBrandContext({
    brand_id: profile.brand_id ?? '',
    name: profile.name,
    introduction: profile.introduction,
    logo_uri: profile.logo_uri,
    logo_mark_uri: profile.logo_mark_uri,
    background_uri: profile.background_uri,
    landing_color: landingColor,
  });

  if (!next) {
    return false;
  }

  persistAdvisorLoginBrandContext(routeId, next);
  return true;
};

export const readAdvisorLoginBrandContext = (): {
  routeId: string;
  profile: AdvisorLoginBrandContext;
} | null => {
  try {
    const stored = localStorage.getItem(ADVISOR_LOGIN_BRAND_CONTEXT_KEY);
    if (!stored) {
      return null;
    }

    const parsed = JSON.parse(stored) as Partial<StoredAdvisorLoginBrandContext>;
    const profile = buildAdvisorLoginBrandContext(parsed.profile);

    if (!profile) {
      clearAdvisorLoginBrandContext();
      return null;
    }

    return {
      routeId: sanitize(parsed.routeId),
      profile,
    };
  } catch (error) {
    console.error('Failed to read advisor login brand context:', error);
    clearAdvisorLoginBrandContext();
    return null;
  }
};

export const readAdvisorLoginBrandContextForRoute = (
  routeFirmId?: string | null,
): AdvisorLoginBrandContext | null => {
  const normalizedRouteFirmId = sanitize(routeFirmId);
  const cached = readAdvisorLoginBrandContext();

  if (!cached || !normalizedRouteFirmId || cached.routeId !== normalizedRouteFirmId) {
    return null;
  }

  return cached.profile;
};

export const readAdvisorLoginBrandContextForAuthChrome = (
  routeFirmId?: string | null,
): AdvisorLoginBrandContext | null => {
  const normalizedRouteFirmId = sanitize(routeFirmId);
  if (normalizedRouteFirmId) {
    return readAdvisorLoginBrandContextForRoute(normalizedRouteFirmId);
  }

  return readAdvisorLoginBrandContext()?.profile ?? null;
};

export const clearAdvisorLoginBrandContext = () => {
  try {
    localStorage.removeItem(ADVISOR_LOGIN_BRAND_CONTEXT_KEY);
  } catch (error) {
    console.error('Failed to clear advisor login brand context:', error);
  }
};
